import { useState, useRef } from 'react';
import {
  View, Text, TouchableOpacity, Modal, Animated,
  StyleSheet, Pressable,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

interface HamburgerDrawerProps {
  role: 'personal' | 'student';
  unreadCount: number;
  conversationTarget: string;
  profileTarget: string;
  financesTarget?: string;
  myWorkoutTarget?: string;
  retentionTarget?: string;
  backToPersonalTarget?: string;
  onSignOut: () => void;
}

export function HamburgerDrawer({
  role,
  unreadCount,
  conversationTarget,
  profileTarget,
  financesTarget,
  myWorkoutTarget,
  retentionTarget,
  backToPersonalTarget,
  onSignOut,
}: HamburgerDrawerProps) {
  const [visible, setVisible] = useState(false);
  const slideAnim = useRef(new Animated.Value(300)).current;
  const router = useRouter();

  function open() {
    setVisible(true);
    Animated.timing(slideAnim, { toValue: 0, duration: 250, useNativeDriver: true }).start();
  }

  function close(callback?: () => void) {
    Animated.timing(slideAnim, { toValue: 300, duration: 200, useNativeDriver: true }).start(() => {
      setVisible(false);
      callback?.();
    });
  }

  function handleChat() {
    close(() => setTimeout(() => router.push(conversationTarget as any), 20));
  }

  function handleProfile() {
    close(() => setTimeout(() => router.push(profileTarget as any), 20));
  }

  function handleFinances() {
    close(() => setTimeout(() => router.push(financesTarget as any), 20));
  }

  function handleMyWorkout() {
    if (!myWorkoutTarget) return;
    close(() => setTimeout(() => router.push(myWorkoutTarget as any), 20));
  }

  function handleRetention() {
    if (!retentionTarget) return;
    close(() => setTimeout(() => router.push(retentionTarget as any), 20));
  }

  function handleBackToPersonal() {
    if (!backToPersonalTarget) return;
    close(() => setTimeout(() => router.replace(backToPersonalTarget as any), 20));
  }

  function handleSignOut() {
    close(() => setTimeout(onSignOut, 20));
  }

  const chatSublabel = role === 'personal' ? 'Mensagens com alunos' : 'Mensagens com seu personal';

  return (
    <>
      <TouchableOpacity
        onPress={open}
        style={styles.trigger}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="menu-outline" size={26} color="#1A1A1A" />
        {unreadCount > 0 && (
          <View style={styles.triggerBadge}>
            <Text style={styles.triggerBadgeText}>
              {unreadCount > 9 ? '9+' : String(unreadCount)}
            </Text>
          </View>
        )}
      </TouchableOpacity>

      <Modal visible={visible} transparent animationType="none" onRequestClose={() => close()}>
        <Pressable style={styles.overlay} onPress={() => close()} />
        <Animated.View style={[styles.drawer, { transform: [{ translateX: slideAnim }] }]}>
          <View style={styles.drawerHeader}>
            <Text style={styles.drawerTitle}>Menu</Text>
            <TouchableOpacity onPress={() => close()} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color="#aaa" />
            </TouchableOpacity>
          </View>

          {/* Chat */}
          <TouchableOpacity style={styles.drawerItem} onPress={handleChat} activeOpacity={0.7}>
            <Ionicons name="chatbubbles-outline" size={22} color="#1A1A1A" />
            <View style={styles.drawerItemText}>
              <Text style={styles.drawerItemLabel}>Chat</Text>
              <Text style={styles.drawerItemSub}>{chatSublabel}</Text>
            </View>
            {unreadCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>
                  {unreadCount > 9 ? '9+' : String(unreadCount)}
                </Text>
              </View>
            )}
          </TouchableOpacity>

          {/* Área de Retenção — personal only */}
          {retentionTarget && (
            <TouchableOpacity style={styles.drawerItem} onPress={handleRetention} activeOpacity={0.7}>
              <Ionicons name="pulse-outline" size={22} color="#1A1A1A" />
              <View style={styles.drawerItemText}>
                <Text style={styles.drawerItemLabel}>Área de Retenção</Text>
                <Text style={styles.drawerItemSub}>Engajamento e ações por aluno</Text>
              </View>
            </TouchableOpacity>
          )}

          {/* Meu treino — personal only */}
          {myWorkoutTarget && (
            <TouchableOpacity style={styles.drawerItem} onPress={handleMyWorkout} activeOpacity={0.7}>
              <Ionicons name="barbell-outline" size={22} color="#1A1A1A" />
              <View style={styles.drawerItemText}>
                <Text style={styles.drawerItemLabel}>Meu treino</Text>
                <Text style={styles.drawerItemSub}>Seus próprios treinos</Text>
              </View>
            </TouchableOpacity>
          )}

          {/* Financeiro — personal only */}
          {financesTarget && (
            <TouchableOpacity style={styles.drawerItem} onPress={handleFinances} activeOpacity={0.7}>
              <Ionicons name="wallet-outline" size={22} color="#1A1A1A" />
              <View style={styles.drawerItemText}>
                <Text style={styles.drawerItemLabel}>Financeiro</Text>
                <Text style={styles.drawerItemSub}>Controle de pagamentos</Text>
              </View>
            </TouchableOpacity>
          )}

          {/* Meu Perfil */}
          <TouchableOpacity style={styles.drawerItem} onPress={handleProfile} activeOpacity={0.7}>
            <Ionicons name="person-circle-outline" size={22} color="#1A1A1A" />
            <View style={styles.drawerItemText}>
              <Text style={styles.drawerItemLabel}>Meu Perfil</Text>
            </View>
          </TouchableOpacity>

          {/* Voltar pro modo Personal — só aparece quando o personal está
              espelhando a UI do aluno (Meu treino) */}
          {backToPersonalTarget && (
            <TouchableOpacity style={styles.drawerItem} onPress={handleBackToPersonal} activeOpacity={0.7}>
              <Ionicons name="arrow-back-circle-outline" size={22} color="#1A1A1A" />
              <View style={styles.drawerItemText}>
                <Text style={styles.drawerItemLabel}>Voltar pro Personal</Text>
                <Text style={styles.drawerItemSub}>Sai do modo treino e volta ao painel</Text>
              </View>
            </TouchableOpacity>
          )}

          {/* Sair */}
          <TouchableOpacity style={styles.drawerItem} onPress={handleSignOut} activeOpacity={0.7}>
            <Ionicons name="log-out-outline" size={22} color="#C8102E" />
            <View style={styles.drawerItemText}>
              <Text style={[styles.drawerItemLabel, styles.signOutLabel]}>Sair</Text>
            </View>
          </TouchableOpacity>
        </Animated.View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  trigger: { position: 'relative', padding: 4 },
  triggerBadge: {
    position: 'absolute', top: 0, right: 0,
    backgroundColor: '#C8102E', borderRadius: 8,
    minWidth: 16, height: 16,
    alignItems: 'center', justifyContent: 'center', paddingHorizontal: 3,
  },
  triggerBadgeText: { color: '#fff', fontSize: 9, fontWeight: '700' },
  overlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  drawer: {
    position: 'absolute', top: 0, right: 0, bottom: 0, width: '72%',
    backgroundColor: '#fff', paddingTop: 60, paddingHorizontal: 16,
    shadowColor: '#000', shadowOffset: { width: -4, height: 0 },
    shadowOpacity: 0.12, shadowRadius: 16, elevation: 8,
  },
  drawerHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 24,
  },
  drawerTitle: {
    fontSize: 11, fontWeight: '700', color: '#888',
    textTransform: 'uppercase', letterSpacing: 0.8,
  },
  drawerItem: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f0f0f0',
  },
  drawerItemText: { flex: 1 },
  drawerItemLabel: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  drawerItemSub: { fontSize: 11, color: '#888', marginTop: 2 },
  signOutLabel: { color: '#C8102E' },
  badge: {
    backgroundColor: '#C8102E', borderRadius: 10,
    minWidth: 20, height: 20,
    alignItems: 'center', justifyContent: 'center', paddingHorizontal: 5,
  },
  badgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },
});
